// screen: Analytics — Overview, Realtime, Audience, Quality, Geography, Devices, Ads, Traffic, Content, Sessions, Protection, Sites, Export, Scheduled reports (Studio §4.10, change p5-analytics; Export: insight-v2 2.3)
import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useLingui } from '@lingui/react/macro';
import { qk, type StatsRealtime, type StatsSeries, type StatsBreakdown, type StatsTraffic, type StatsPercentile } from '@viewstream/api-client';
import { Chart, KpiTile, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from '@viewstream/ui';
import { fmtBitrate, fmtDateTime, fmtNumber } from '@viewstream/i18n';
import { api } from '~/app/api';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { appTitle } from '~/app/brand';
import { ContentTab, type ContentPick } from '~/app/AnalyticsContent';
import { SessionsTab } from '~/app/AnalyticsSessions';
import { SitesTab } from '~/app/AnalyticsSites';
import { QoETab } from '~/app/AnalyticsQoE';
import { ReportsTab } from '~/app/AnalyticsReports';
import { ExportTab } from '~/app/AnalyticsExport';
import { AdsTab, AudienceTab, CsvButton, DevicesTab, Failure, FilterBar, GeographyTab, OverviewTab, ProtectionTab, RANGE_INTERVAL, Section, rangeWindow, useFmt, type Filters, type Range } from '~/app/analytics';

type Tab = 'overview' | 'realtime' | 'audience' | 'quality' | 'geography' | 'devices' | 'ads' | 'traffic' | 'content' | 'sessions' | 'protection' | 'sites' | 'export' | 'reports';
const TABS: Tab[] = ['overview', 'realtime', 'audience', 'quality', 'geography', 'devices', 'ads', 'traffic', 'content', 'sessions', 'protection', 'sites', 'export', 'reports'];
const ID = /^[0-9a-f-]{36}$/;
interface Search { tab?: Tab; range?: Range; channel?: string; country?: string; asset?: string; clip?: string; site?: string }

export const Route = createFileRoute('/_layout/t/$slug/analytics')({
  component: Screen,
  validateSearch: (s: Record<string, unknown>): Search => ({
    tab: TABS.includes(s['tab'] as Tab) ? (s['tab'] as Tab) : undefined,
    range: s['range'] === '1h' || s['range'] === '7d' || s['range'] === '30d' ? s['range'] : undefined,
    channel: typeof s['channel'] === 'string' && s['channel'] ? s['channel'] : undefined,
    country: typeof s['country'] === 'string' && /^[A-Z]{2}$/.test(s['country']) ? s['country'] : undefined,
    asset: typeof s['asset'] === 'string' && ID.test(s['asset']) ? s['asset'] : undefined,
    clip: typeof s['clip'] === 'string' && ID.test(s['clip']) ? s['clip'] : undefined,
    site: typeof s['site'] === 'string' && ID.test(s['site']) ? s['site'] : undefined,
  }),
});

function Screen() {
  const { t } = useLingui();
  const search = Route.useSearch();
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const set = (patch: Partial<Search>) => void navigate({ to: '/t/$slug/analytics', params: { slug }, search: { ...search, ...patch }, replace: true } as never);
  const tab: Tab = search.tab ?? 'overview';
  const f: Filters = { range: search.range ?? '24h', channel: search.channel, country: search.country, asset: search.asset, clip: search.clip };
  const onFilters = (n: Filters) => set({ range: n.range === '24h' ? undefined : n.range, channel: n.channel, country: n.country, asset: n.asset, clip: n.clip });
  // Content drill-down: one item at a time (channel / video / clip); null = back to the rankings
  const onPick = (p: ContentPick | null) => set({ tab: 'content', channel: p?.kind === 'channel' ? p.id : undefined, asset: p?.kind === 'asset' ? p.id : undefined, clip: p?.kind === 'clip' ? p.id : undefined });
  React.useEffect(() => { document.title = appTitle(t`Analytics`); }, [t]);
  const labels: Record<Tab, string> = {
    overview: t`Overview`, realtime: t`Realtime`, audience: t`Audience`, quality: t`Viewing quality`, geography: t`Geography`,
    devices: t`Devices`, ads: t`Ads`, traffic: t`Traffic`, content: t`Content`, sessions: t`Sessions`, protection: t`Protection`, sites: t`Sites`,
    export: t`Export`, reports: t`Scheduled reports`,
  };
  return (
    <div>
      <h1 className="mb-4 text-2xl font-semibold">{t`Analytics`}</h1>
      <Tabs value={tab} onValueChange={(v) => set({ tab: v === 'overview' ? undefined : (v as Tab) })}>
        <TabsList className="flex flex-wrap">
          {TABS.map((k) => <TabsTrigger key={k} value={k}>{labels[k]}</TabsTrigger>)}
        </TabsList>
        {tab !== 'realtime' && tab !== 'reports' ? <div className="pt-4"><FilterBar value={f} onChange={onFilters} /></div> : null}
        <TabsContent value="overview" className="pt-4"><OverviewTab f={f} /></TabsContent>
        <TabsContent value="realtime" className="pt-4"><Realtime /></TabsContent>
        <TabsContent value="audience" className="pt-4"><AudienceTab f={f} /></TabsContent>
        <TabsContent value="quality" className="pt-4"><QoETab f={f} /></TabsContent>
        <TabsContent value="geography" className="pt-4"><GeographyTab f={f} /></TabsContent>
        <TabsContent value="devices" className="pt-4"><DevicesTab f={f} /></TabsContent>
        <TabsContent value="ads" className="pt-4"><AdsTab f={f} /></TabsContent>
        <TabsContent value="traffic" className="pt-4"><Traffic f={f} /></TabsContent>
        <TabsContent value="content" className="pt-4"><ContentTab f={f} onPick={onPick} /></TabsContent>
        <TabsContent value="sessions" className="pt-4"><SessionsTab f={f} /></TabsContent>
        <TabsContent value="protection" className="pt-4"><ProtectionTab f={f} /></TabsContent>
        <TabsContent value="sites" className="pt-4"><SitesTab f={f} site={search.site} onSite={(id) => set({ site: id })} /></TabsContent>
        <TabsContent value="export" className="pt-4"><ExportTab f={f} /></TabsContent>
        <TabsContent value="reports" className="pt-4"><ReportsTab /></TabsContent>
      </Tabs>
    </div>
  );
}



/** Time axis and tooltip in the tenant time zone, 24 h (ECharts itself would use the browser's zone). */
export function lineOption(points: { t: string; value: number }[], name: string, fmt: (v: number) => string, where: { locale: string; tz: string } = { locale: 'en', tz: 'Asia/Jerusalem' }, days = false) {
  const tick = (ms: number) => fmtDateTime(new Date(ms), where, days ? { dateStyle: undefined, timeStyle: undefined, day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' } : { dateStyle: undefined, timeStyle: 'short' });
  const full = (ms: number) => fmtDateTime(new Date(ms), where, { dateStyle: 'short', timeStyle: 'short' });
  return {
    grid: { left: 56, right: 16, top: 16, bottom: 28 },
    tooltip: {
      trigger: 'axis', valueFormatter: (v: number) => fmt(v),
      axisPointer: { label: { formatter: (p: { value: number }) => full(p.value) } },
      formatter: (ps: { value: [string, number]; marker: string; seriesName: string }[]) => {
        const p = ps[0];
        return p ? `${full(new Date(p.value[0]).getTime())}<br/>${p.marker}${p.seriesName}: <b>${fmt(p.value[1])}</b>` : '';
      },
    },
    xAxis: { type: 'time', axisLabel: { formatter: (v: number) => tick(v), hideOverlap: true } },
    yAxis: { type: 'value', axisLabel: { formatter: (v: number) => fmt(v) } },
    series: [{ type: 'line', name, showSymbol: false, areaStyle: { opacity: 0.15 }, data: points.map((p) => [p.t, p.value]) }],
  };
}

/* ------------------------------------------------------------------ Realtime */

function Realtime() {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const { locale, tz } = useLocale();
  // same cache entry the Dashboard strip uses: the stats.realtime SSE event keeps it fresh, polling only without SSE
  const rt = useQuery({
    queryKey: qk.statsRealtime(tenant.slug),
    queryFn: async () => (await api.GET('/v1/stats/realtime')).data as StatsRealtime,
    refetchInterval: (query) => (query.state.dataUpdatedAt > Date.now() - 12_000 ? false : 10_000),
    retry: false,
  });
  const hour = rangeWindow('1h');
  const conc = useQuery({
    queryKey: qk.stats(tenant.slug, 'timeseries', { metric: 'concurrent', from: hour.from }),
    queryFn: async () => (await api.GET('/v1/stats/timeseries', { params: { query: { metric: 'concurrent', interval: '1m', ...hour } } })).data as StatsSeries,
    refetchInterval: 30_000,
    retry: false,
  });
  const geo = useQuery({
    queryKey: qk.stats(tenant.slug, 'breakdown', { dimension: 'country', from: hour.from }),
    queryFn: async () => (await api.GET('/v1/stats/breakdown', { params: { query: { dimension: 'country', metric: 'plays', limit: 10, ...hour } } })).data as StatsBreakdown,
    refetchInterval: 60_000,
    retry: false,
  });
  if (rt.isPending) return <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <KpiTile key={i} label={<Skeleton className="h-3 w-16" />} value="" loading />)}</div>;
  if (rt.isError) return <Failure error={rt.error} what={t`Realtime statistics`} onRetry={() => void rt.refetch()} />;
  const d = rt.data;
  const n = (v: number | undefined) => fmtNumber(v ?? 0, locale);
  const updated = d.at ? fmtDateTime(d.at, { locale, tz }, { dateStyle: undefined, timeStyle: 'medium' }) : '—';
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-live="polite">
        <KpiTile label={t`Concurrent viewers`} value={n(d.concurrent?.total)} hint={`${t`Live`} ${n(d.concurrent?.live)} · VOD ${n(d.concurrent?.vod)}`} />
        <KpiTile label={t`Plays (5 min)`} value={n(d.plays_last_5m)} hint={`${t`Last hour`} ${n(d.plays_last_hour)}`} />
        <KpiTile label={t`Gbps now`} value={fmtBitrate((d.gbps?.total ?? 0) * 1e9, locale)} />
        <KpiTile label={t`Errors / min`} value={n(d.fatal_errors_per_min)} hint={t`Updated ${updated}`} />
      </div>
      <Section id="rt-conc" title={t`Concurrent viewers — last hour`}>
        {conc.isPending ? <Skeleton className="h-60 w-full" /> : conc.isError ? <Failure error={conc.error} what={t`Concurrency`} onRetry={() => void conc.refetch()} /> : (
          <Chart ariaLabel={t`Concurrent viewers per minute over the last hour`} option={lineOption(conc.data.points, t`Concurrent viewers`, (v) => fmtNumber(v, locale), { locale, tz })} />
        )}
      </Section>
      <div className="grid gap-6 lg:grid-cols-2">
        <Section id="rt-top" title={t`Top channels now`}>
          {(d.top_channels?.length ?? 0) === 0 ? <p className="text-sm text-fg-muted">{t`Nobody is watching a channel right now.`}</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-start text-fg-muted"><th className="py-1 text-start font-medium">{t`Channel`}</th><th className="py-1 text-end font-medium">{t`Viewers`}</th></tr></thead>
              <tbody>{d.top_channels.map((c) => <tr key={c.id} className="border-t border-border"><td className="py-1.5">{c.title ?? c.slug}</td><td className="py-1.5 text-end tabular-nums">{n(c.concurrent)}</td></tr>)}</tbody>
            </table>
          )}
          {(d.top_assets?.length ?? 0) > 0 ? (
            <>
              <h3 className="mb-2 mt-4 text-sm font-semibold">{t`Top videos now`}</h3>
              <ul className="text-sm">{d.top_assets.map((a) => <li key={a.id} className="flex justify-between border-t border-border py-1.5"><span className="truncate">{a.title ?? a.id}</span><span className="tabular-nums">{n(a.concurrent)}</span></li>)}</ul>
            </>
          ) : null}
        </Section>
        <Section id="rt-geo" title={t`Plays by country — last hour`}>
          {geo.isPending ? <Skeleton className="h-40 w-full" /> : geo.isError ? <Failure error={geo.error} what={t`Countries`} onRetry={() => void geo.refetch()} /> : geo.data.rows.length === 0 ? (
            <p className="text-sm text-fg-muted">{t`No plays in the last hour.`}</p>
          ) : <CountryList rows={geo.data.rows} />}
        </Section>
      </div>
      <Section id="rt-delivery" title={t`Delivery now`}>
        <div className="grid gap-6 lg:grid-cols-3">
          <RtTable title={t`By hostname`} head={t`Gbps`} empty={t`No traffic in the last minute.`}
            rows={Object.entries(d.gbps?.by_distribution ?? {}).sort((x, y) => y[1] - x[1]).slice(0, 10).map(([h, v]) => [h, fmtBitrate(v * 1e9, locale)])} ltrKeys />
          <RtTable title={t`By viewer network (ASN)`} head={t`Gbps`} empty={t`No traffic in the last minute.`}
            rows={(d.gbps?.top_asn ?? []).map((a) => [a.asn ? `AS${a.asn}` : t`Unknown`, fmtBitrate(a.gbps * 1e9, locale)])} ltrKeys />
          <RtTable title={t`By delivery path`} head={t`Viewers`} empty={t`Nobody is watching right now.`}
            rows={Object.entries(d.concurrent_by_pathway ?? {}).sort((x, y) => y[1] - x[1]).map(([p, v]) => [p === 'il' ? t`ViewStream edges` : p, n(v)])} />
        </div>
      </Section>
    </div>
  );
}

/** A small two-column table for the realtime delivery breakdowns (v3 §9.9). */
function RtTable({ title, head, rows, empty, ltrKeys }: { title: string; head: string; rows: [string, string][]; empty: string; ltrKeys?: boolean }) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold">{title}</h3>
      {rows.length === 0 ? <p className="text-sm text-fg-muted">{empty}</p> : (
        <table className="w-full text-sm">
          <thead><tr className="text-fg-muted"><th className="py-1 text-start font-medium" /><th className="py-1 text-end font-medium">{head}</th></tr></thead>
          <tbody>{rows.map(([k, v]) => (
            <tr key={k} className="border-t border-border"><td className={ltrKeys ? 'ltr-isolate py-1.5' : 'py-1.5'}>{k}</td><td className="py-1.5 text-end tabular-nums">{v}</td></tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}

function CountryList({ rows }: { rows: StatsBreakdown['rows'] }) {
  const { locale } = useLocale();
  const names = React.useMemo(() => { try { return new Intl.DisplayNames([locale], { type: 'region' }); } catch { return null; } }, [locale]);
  const label = (k: string) => (k && k !== '--' ? (names?.of(k) ?? k) : '—');
  return (
    <ul className="space-y-2 text-sm">
      {rows.map((r) => (
        <li key={r.key || '-'}>
          <div className="flex justify-between"><span>{label(r.key)}</span><span className="tabular-nums">{fmtNumber(r.value, locale)} · {fmtNumber(r.share, locale, { style: 'percent', maximumFractionDigits: 0 })}</span></div>
          <div className="mt-1 h-1.5 rounded bg-border" aria-hidden><div className="h-1.5 rounded bg-primary" style={{ width: `${Math.max(2, Math.round(r.share * 100))}%` }} /></div>
        </li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------------ Traffic */

function Traffic({ f }: { f: Filters }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const { locale, tz } = useLocale();
  const F = useFmt();
  const pathLabel = usePathLabel();
  const range = f.range;
  const w = { ...rangeWindow(range), ...(f.country ? { country: f.country } : {}) };
  const refetchInterval = range === '1h' ? 30_000 : 120_000;
  const tq = (group_by?: 'path_type' | 'site' | 'host' | 'country') => ({
    queryKey: qk.stats(tenant.slug, 'traffic', { ...w, group_by, range }),
    queryFn: async () => (await api.GET('/v1/stats/traffic', { params: { query: { ...(group_by ? { group_by } : {}), ...w } } })).data as StatsTraffic,
    refetchInterval, retry: false,
  });
  const totals = useQuery(tq());
  const byPath = useQuery(tq('path_type'));
  const bySite = useQuery(tq('site'));
  const byHost = useQuery(tq('host'));
  const byCountry = useQuery(tq('country'));
  const gbps = useQuery({
    queryKey: qk.stats(tenant.slug, 'timeseries', { metric: 'gbps', ...w, range }),
    queryFn: async () => (await api.GET('/v1/stats/timeseries', { params: { query: { metric: 'gbps', interval: RANGE_INTERVAL[range], ...w } } })).data as StatsSeries,
    refetchInterval, retry: false,
  });
  const p95 = useQuery({
    queryKey: qk.stats(tenant.slug, 'traffic_p95', { ...w, range }),
    queryFn: async () => (await api.GET('/v1/stats/traffic/percentile', { params: { query: w } })).data as StatsPercentile,
    refetchInterval, retry: false,
  });
  const regions = React.useMemo(() => { try { return new Intl.DisplayNames([locale], { type: 'region' }); } catch { return null; } }, [locale]);
  const tot = totals.data?.rows?.[0];
  return (
    <div className="space-y-6">
      {totals.isPending ? <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">{[0, 1, 2, 3, 4].map((i) => <KpiTile key={i} label={<Skeleton className="h-3 w-16" />} value="" loading />)}</div>
        : totals.isError ? <Failure error={totals.error} what={t`Traffic statistics`} onRetry={() => void totals.refetch()} /> : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <KpiTile label={t`Delivered`} value={F.bytes(tot?.bytes ?? 0)} hint={`${fmtNumber(tot?.requests ?? 0, locale)} ${t`requests`}`} />
            <KpiTile label={t`Average Gbps`} value={F.gbps(tot?.gbps ?? 0)} />
            <KpiTile label={t`95th percentile`} value={p95.data ? F.gbps(p95.data.p95_gbps) : '—'} hint={p95.data ? t`Peak ${F.gbps(p95.data.peak_gbps)} · ${Math.round(p95.data.sample_secs / 60)}-min samples` : undefined} />
            <KpiTile label={t`Cache hit ratio`} value={F.pct(tot?.hit_ratio_bytes ?? 0)} hint={`${t`Requests`} ${F.pct(tot?.hit_ratio_requests ?? 0)}`} />
            <KpiTile label={t`Origin egress`} value={F.bytes(tot?.origin_bytes ?? 0)} />
          </div>
        )}
      <Section id="tr-gbps" title={t`Delivered Gbps`} actions={gbps.data ? <CsvButton name="gbps" header={['t', 'gbps']} rows={gbps.data.points.map((p) => [p.t, p.value])} /> : null}>
        {gbps.isPending ? <Skeleton className="h-60 w-full" /> : gbps.isError ? <Failure error={gbps.error} what={t`Gbps`} onRetry={() => void gbps.refetch()} /> : (
          <Chart ariaLabel={t`Delivered gigabits per second over the selected range`} option={lineOption(gbps.data.points, 'Gbps', (v) => fmtNumber(v, locale, { maximumFractionDigits: 3 }), { locale, tz }, range === '7d' || range === '30d')} />
        )}
      </Section>
      <div className="grid gap-6 lg:grid-cols-2">
        <Section id="tr-path" title={t`By content type`} actions={<TrafficCsv name="traffic-by-type" q={byPath} />}><TrafficTable q={byPath} bytes={F.bytes} pct={F.pct} label={pathLabel} /></Section>
        <Section id="tr-site" title={t`By edge site`} actions={<TrafficCsv name="traffic-by-site" q={bySite} />}><TrafficTable q={bySite} bytes={F.bytes} pct={F.pct} label={(g) => g || '—'} /></Section>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Section id="tr-host" title={t`By hostname`} actions={<TrafficCsv name="traffic-by-host" q={byHost} />}><TrafficTable q={byHost} bytes={F.bytes} pct={F.pct} label={(g) => g || '—'} /></Section>
        <Section id="tr-country" title={t`By country`} actions={<TrafficCsv name="traffic-by-country" q={byCountry} />}><TrafficTable q={byCountry} bytes={F.bytes} pct={F.pct} label={(g) => (g && g !== '--' ? regions?.of(g) ?? g : '—')} /></Section>
      </div>
    </div>
  );
}

function TrafficCsv({ name, q }: { name: string; q: ReturnType<typeof useQuery<StatsTraffic>> }) {
  const rows = q.data?.rows ?? [];
  return <CsvButton name={name} header={['group', 'requests', 'bytes', 'gbps', 'hit_ratio_bytes', 'origin_bytes']} rows={rows.map((r) => [r.group ?? '', r.requests, r.bytes, r.gbps, r.hit_ratio_bytes, r.origin_bytes])} />;
}

/** Content-type labels. Built inside the component: the Lingui `t` macro only compiles where `t` comes from
 *  useLingui() in scope — as a plain parameter it returned "" and every type label was blank (QA 2026-09-28). */
function usePathLabel(): (g: string) => string {
  const { t } = useLingui();
  const names: Record<string, string> = {
    live: t`Live`, vod: t`VOD`, rec: t`Catch-up`, manifest: t`Manifests`, player: t`Player`, image: t`Images`, beacon: t`Beacons`, api: t`API`,
  };
  const other = t`Other`;
  return (g: string) => names[g] ?? other;
}

function TrafficTable({ q, bytes, pct, label }: { q: ReturnType<typeof useQuery<StatsTraffic>>; bytes: (v: number) => string; pct: (v: number) => string; label: (g: string) => string }) {
  const { t } = useLingui();
  if (q.isPending) return <Skeleton className="h-32 w-full" />;
  if (q.isError) return <Failure error={q.error} what={t`Traffic`} onRetry={() => void q.refetch()} />;
  const rows = [...(q.data?.rows ?? [])].sort((a, b) => b.bytes - a.bytes);
  if (rows.length === 0) return <p className="text-sm text-fg-muted">{t`No traffic in this range.`}</p>;
  return (
    <table className="w-full text-sm">
      <thead><tr className="text-fg-muted"><th className="py-1 text-start font-medium">{t`Type`}</th><th className="py-1 text-end font-medium">{t`Delivered`}</th><th className="py-1 text-end font-medium">{t`Hit ratio`}</th></tr></thead>
      <tbody>{rows.map((r) => <tr key={r.group ?? '-'} className="border-t border-border"><td className="py-1.5">{label(r.group ?? '')}</td><td className="py-1.5 text-end tabular-nums">{bytes(r.bytes)}</td><td className="py-1.5 text-end tabular-nums">{pct(r.hit_ratio_bytes)}</td></tr>)}</tbody>
    </table>
  );
}
